/** A staged configuration write belongs to its current account, page and version. */
import { useEffect, useRef } from "react";
import { api, ApiError } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import type { SetupCompetition } from "./model";
export function useSetupScope(competitionId: string, version?: number) {
  const userId = useAuthStore((state) => state.user?.id);
  const latest = useRef({ competitionId, version, userId });
  latest.current = { competitionId, version, userId };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return () => {
    const submitted = { competitionId, version, userId };
    return () =>
      mounted.current &&
      latest.current.competitionId === submitted.competitionId &&
      latest.current.version === submitted.version &&
      latest.current.userId === submitted.userId &&
      useAuthStore.getState().user?.id === submitted.userId;
  };
}
export function useSetupWrite(
  competition: SetupCompetition | null,
  onRefresh: () => Promise<void>,
  captureExtra?: () => () => boolean,
) {
  const userId = useAuthStore((state) => state.user?.id);
  const capture = useSetupScope(competition?.id ?? "", competition?.version);
  const latest = useRef({ competition, onRefresh, capture, userId, captureExtra });
  latest.current = { competition, onRefresh, capture, userId, captureExtra };
  const competitionId = competition?.id;
  return async (patch: Record<string, unknown>) => {
    const current = latest.current;
    const sameIdentity = () =>
      latest.current.competition?.id === competitionId &&
      latest.current.userId === userId &&
      useAuthStore.getState().user?.id === userId;
    if (!current.competition || !sameIdentity())
      throw new ApiError(409, "This action belongs to a previous account or event.", "stale_send");
    const scopeGuard = current.capture();
    const extraGuard = current.captureExtra?.();
    const canSend = () => scopeGuard() && (!extraGuard || extraGuard());
    await api.put(
      `/api/competitions/${current.competition.id}`,
      {
        expected_version: current.competition.version,
        ...patch,
      },
      {
        canSend,
        canRetry: () => false,
        onConflict: () => {
          if (sameIdentity() && latest.current.capture()() && (!extraGuard || extraGuard()))
            return latest.current.onRefresh();
        },
      },
    );
    if (sameIdentity() && latest.current.capture()() && (!extraGuard || extraGuard()))
      await latest.current.onRefresh();
  };
}
